import SortIcon from '@assets/icons/sort.svg?react'
import { Box } from '@chakra-ui/react'
import { useMemo, useState } from 'react'
import { ArrowDown, ArrowUp } from 'react-feather'

type NestedKeyOf<T> = keyof T | [keyof T, ...string[]]

function useTableSort<T>(
  initialData: T[],
  initialKey: NestedKeyOf<T> | string | null = null,
  initialDirection: 'asc' | 'desc' | null = null,
  customSortFunctions?: Record<string, (a: T, b: T) => number>
) {
  const getPath = (pathArray: NestedKeyOf<T> | string): string => {
    if (Array.isArray(pathArray)) return pathArray.join('.')
    else return pathArray as string
  }

  const [sortConfig, setSortConfig] = useState<{ key: string | null; direction: 'asc' | 'desc' | null }>({
    key: initialKey ? getPath(initialKey) : null,
    direction: initialDirection,
  })

  const sortedData = useMemo(() => {
    const sortableItems = [...initialData]

    if (sortConfig.key && sortConfig.direction) {
      sortableItems.sort((a, b) => {
        const hasCustomSortFunction = customSortFunctions && typeof sortConfig.key === 'string' && sortConfig.key in customSortFunctions

        if (hasCustomSortFunction) {
          const result = customSortFunctions[sortConfig.key!](a, b)
          return sortConfig.direction === 'asc' ? result : -result
        } else {
          return applyDefaultSort(a, b, sortConfig.key!, sortConfig.direction!)
        }
      })
    }

    return sortableItems
  }, [initialData, sortConfig.key, sortConfig.direction, customSortFunctions])

  const requestSort = (key: string, onChange?: (key: string, direction: 'asc' | 'desc') => void) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc'
    onChange?.(key, direction)
    setSortConfig({ key, direction })
  }

  const SortArrows = ({
    headKey,
    onChange,
  }: { headKey: NestedKeyOf<T> | string; onChange?: (key: string, direction: 'asc' | 'desc') => void }) => {
    const pathKey = getPath(headKey)
    return (
      <Box cursor='pointer' onClick={() => requestSort(pathKey, onChange)}>
        {sortConfig.key === pathKey ? (
          sortConfig.direction === 'asc' ? (
            <ArrowUp size='16px' />
          ) : sortConfig.direction === 'desc' ? (
            <ArrowDown size='16px' />
          ) : (
            <SortIcon />
          )
        ) : (
          <SortIcon />
        )}
      </Box>
    )
  }

  return { sortedData, sortConfig, requestSort, SortArrows }
}

export default useTableSort

// Function to access a nested property by path (e.g., "quantity.value")
function getNestedPropertyValue<T>(obj: T, path: string): any {
  return path.split('.').reduce((acc, part) => acc?.[part], obj)
}

// Function to apply default sorting based on property values
function applyDefaultSort<T>(a: T, b: T, key: string, direction: 'asc' | 'desc'): number {
  const isComparingString = typeof a === 'string' || typeof b === 'string'
  const fallback = isComparingString ? '\uffff' : Number.NEGATIVE_INFINITY

  const aValue = getNestedPropertyValue(a, key) ?? fallback
  const bValue = getNestedPropertyValue(b, key) ?? fallback

  if (isComparingString) {
    return String(aValue).localeCompare(String(bValue))
  }

  if (aValue < bValue) {
    return direction === 'asc' ? -1 : 1
  }
  if (aValue > bValue) {
    return direction === 'asc' ? 1 : -1
  }
  return 0
}
